import { useEffect, useState } from "react";

const KEY = "healthos_attention_toast_hold_until";
// Backstop only. The listeners below carry same-tab changes immediately; this
// catches a hold expiring on its own, and another tab setting one.
const POLL_MS = 500;

const listeners = new Set<() => void>();

export function holdAttentionToasts(durationMs: number): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(KEY, String(Date.now() + Math.max(0, durationMs)));
  } catch {
    /* storage unavailable */
  }
  // Synchronously, before the caller navigates. The toast surface is mounted
  // app-wide and never remounts on navigation, so polling alone let a handoff
  // paint on the destination for up to a full interval before the hold that
  // was set for it was noticed.
  for (const listener of listeners) listener();
}

function readHoldUntil(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const value = Number(raw);
    if (!Number.isFinite(value) || value <= Date.now()) return null;
    return value;
  } catch {
    return null;
  }
}

export function useAttentionToastGated(): boolean {
  const [gated, setGated] = useState<boolean>(() => readHoldUntil() !== null);
  useEffect(() => {
    const check = () => {
      const isGated = readHoldUntil() !== null;
      setGated((prev) => (prev === isGated ? prev : isGated));
    };
    // A hold expiring is not an event, so wake exactly when it does.
    let expiry: number | undefined;
    const arm = () => {
      window.clearTimeout(expiry);
      const until = readHoldUntil();
      if (until !== null) expiry = window.setTimeout(check, until - Date.now() + 20);
    };
    const onChange = () => {
      check();
      arm();
    };
    onChange();
    listeners.add(onChange);
    const interval = window.setInterval(onChange, POLL_MS);
    return () => {
      listeners.delete(onChange);
      window.clearInterval(interval);
      window.clearTimeout(expiry);
    };
  }, []);
  return gated;
}
